import { DIRECT_CHAT_ROOM } from "@circle-react/components/constants";
import {
  useUnreadChatRoomsStore,
  selectChatRoomMessageActions,
} from "@circle-react/hooks/chatsV2/store";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import type { ChatRoomParticipant } from "@/react/types/Chat/ChatRoomParticipant";

import { useUnreadChatRoomUuids } from "../chatsV2/useUnreadChatRoomUuids";
import { useElectronNotification } from "../notifications/useElectronNotification";
import { useChatRoomParticipant } from "./useChatRoomParticipant";

interface UseChatHeaderApiProps {
  communityId: string;
}

interface LatestChatRoomMessage {
  body: string;
  chat_room_kind: string;
  chat_room_uuid: string;
  embedded: boolean;
  id: number;
  in_review?: boolean;
  name: string;
  sender: ChatRoomMessage["sender"];
}

interface ChatRoomChannelEventBase {
  chat_room_uuid?: string;
  chat_room_uuids?: string[];
}

type ChatRoomChannelEvent =
  | (ChatRoomChannelEventBase & {
      event: "newMessage";
      json_message: LatestChatRoomMessage;
    })
  | (ChatRoomChannelEventBase & {
      event: "updatedMessage";
      json_message: ChatRoomMessage;
    })
  | (ChatRoomChannelEventBase & { event: "chatRoomRead" })
  | (ChatRoomChannelEventBase & { event: "chatMarkAllAsRead" });

interface UnreadChatRoomsData {
  chat_room_uuids: string[];
}

export const useChatHeaderApi = ({ communityId }: UseChatHeaderApiProps) => {
  const { setUnreadChatRoomUuids } = useUnreadChatRoomsStore();
  const { updateMessage, setScrollInfo } = useChatStore(
    selectChatRoomMessageActions,
  );
  const { sendNotificationToDesktopApp } = useElectronNotification();

  const unreadChatRoomUuids = useUnreadChatRoomUuids({ communityId });
  const [newUnreadChatRoomBatch, setnewUnreadChatRoomBatch] = useState<
    string[]
  >([]);
  const [chatRoomUuid, setChatRoomUuid] = useState<string | null>(null);

  const unreadChatRoomUuidsRef = useRef<string[]>(unreadChatRoomUuids);
  const newUnreadChatRoomBatchRef = useRef(newUnreadChatRoomBatch);

  const mutation = useChatRoomParticipant(chatRoomUuid);

  const onNewMessage = async (
    unreadChatRoomUuidsParam: string[],
    eventData: Extract<ChatRoomChannelEvent, { event: "newMessage" }>,
    communityMemberId: number,
  ) => {
    const latestChatRoomMessage = eventData.json_message;
    if (!latestChatRoomMessage) return;

    const {
      chat_room_uuid,
      chat_room_kind,
      sender,
      embedded: isEmbedded,
    } = latestChatRoomMessage;
    const openChatRoomUuid = window.location.pathname?.split("messages/")[1];

    const { community_member_id } = sender;
    const isChatRoomOpen = openChatRoomUuid === chat_room_uuid;
    const isDirectChatRoom = chat_room_kind === DIRECT_CHAT_ROOM;

    // if the sender is not the current user, send a notification to desktop app
    if (community_member_id !== communityMemberId) {
      sendNotificationToDesktopApp({
        title: `${latestChatRoomMessage.sender.name} ${t("desktop_app.notifications.new_message_text")}`,
        body: latestChatRoomMessage.body,
        metadata: latestChatRoomMessage as unknown as Record<string, unknown>,
        type: "message",
      });
    }

    if (isEmbedded || isChatRoomOpen) {
      return;
    }

    setChatRoomUuid(chat_room_uuid);

    let currentChatRoomParticipant: ChatRoomParticipant | undefined | null;
    if (!isDirectChatRoom) {
      const result = await mutation.mutateAsync();
      currentChatRoomParticipant = result?.chat_room_participant;
    }

    //community_member_id: sender of the message
    //communityMemberId: current logged in community member
    if (
      community_member_id === Number(communityMemberId) ||
      currentChatRoomParticipant?.muted
    ) {
      return;
    }

    const messageChatRoom = unreadChatRoomUuidsParam?.find(
      uuid => uuid === chat_room_uuid,
    );

    if (!messageChatRoom) {
      setnewUnreadChatRoomBatch([
        ...newUnreadChatRoomBatchRef.current,
        chat_room_uuid,
      ]);
      setTimeout(() => handleTimeoutOver(), 2000);
    }
  };

  const handleTimeoutOver = () => {
    setUnreadChatRoomUuids(Number(communityId), [
      ...new Set([
        ...unreadChatRoomUuidsRef.current,
        ...newUnreadChatRoomBatchRef.current,
      ]),
    ]);
    setnewUnreadChatRoomBatch([]); // Clear the batch after processing
  };

  const onChatRoomRead = (
    unreadChatRoomUuidsParam: string[],
    eventData: Extract<ChatRoomChannelEvent, { event: "chatRoomRead" }>,
  ) => {
    const { chat_room_uuid } = eventData;
    if (!chat_room_uuid) return;

    const updatedUnreadChatRoomUuidsRef = unreadChatRoomUuidsParam?.filter(
      uuid => uuid !== chat_room_uuid,
    );

    const updatednewUnreadChatRoomBatch =
      newUnreadChatRoomBatchRef?.current?.filter(
        uuid => uuid !== chat_room_uuid,
      );

    setnewUnreadChatRoomBatch(updatednewUnreadChatRoomBatch);
    setUnreadChatRoomUuids(Number(communityId), updatedUnreadChatRoomUuidsRef);
  };

  const onChatMarkAllAsRead = (
    unreadChatRoomUuidsParam: string[],
    eventData: Extract<ChatRoomChannelEvent, { event: "chatMarkAllAsRead" }>,
  ) => {
    const { chat_room_uuids: chatRoomUuids } = eventData;
    if (!chatRoomUuids) return;
    const unreadChatRoomUuids = unreadChatRoomUuidsParam.filter(
      uuid => !chatRoomUuids.includes(uuid),
    );
    setUnreadChatRoomUuids(Number(communityId), unreadChatRoomUuids);
  };

  const onEventReceive = (
    unreadChatRoomUuidsParam: string[],
    eventData: ChatRoomChannelEvent,
    communityMemberId: number,
  ) => {
    switch (eventData.event) {
      case "newMessage":
        void onNewMessage(
          unreadChatRoomUuidsParam,
          eventData,
          communityMemberId,
        );
        break;
      case "updatedMessage": {
        const { json_message: updatedMessage } = eventData;
        setScrollInfo(updatedMessage.chat_room_uuid, { action: "noScroll" });
        updateMessage(updatedMessage.chat_room_uuid, updatedMessage);
        useChatStore.getState().threads.updateMessage(updatedMessage);
        break;
      }
      case "chatRoomRead":
        onChatRoomRead(unreadChatRoomUuidsParam, eventData);
        break;
      case "chatMarkAllAsRead":
        onChatMarkAllAsRead(unreadChatRoomUuidsParam, eventData);
        break;
    }
  };

  const fetchUnreadChatRoomUuids = (communityIdParam: string) => {
    reactQueryGet<UnreadChatRoomsData>(
      internalApi.chatRooms.unreadChatRooms({
        params: {
          community_id: communityIdParam,
        },
      }),
    )
      .then(data => {
        if (data.chat_room_uuids) {
          setUnreadChatRoomUuids(
            Number(communityIdParam),
            data.chat_room_uuids,
          );
        }
      })
      .catch(error => {
        console.error(error);
      });
  };

  // Update refs whenever state changes
  unreadChatRoomUuidsRef.current = unreadChatRoomUuids;
  newUnreadChatRoomBatchRef.current = newUnreadChatRoomBatch;

  return {
    unreadChatRoomUuids,
    onEventReceive,
    fetchUnreadChatRoomUuids,
    unreadChatRoomUuidsRef,
    newUnreadChatRoomBatch,
    newUnreadChatRoomBatchRef,
    setUnreadChatRoomUuids,
  };
};
